import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useCommunityUnread } from '../hooks/useCommunityUnread';
import MentionInput from '../components/MentionInput';
import PostContent from '../components/PostContent';
import { 
  MessageSquare, ThumbsUp, ThumbsDown, Send, Trash2, 
  ChevronDown, User, Clock, Truck, Building2, Headphones, RefreshCw,
  ImagePlus, Film, X, ArrowLeft, Pencil, Save, Link2, Reply
} from 'lucide-react';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/media';

const ROLE_CONFIG = {
  driver: { label: 'Driver', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', icon: Truck },
  carrier_owner: { label: 'Carrier', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: Building2 },
  dispatcher: { label: 'Dispatcher', color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', icon: Headphones },
  admin: { label: 'Admin', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: User }
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const MediaDisplay = ({ media }) => {
  if (!media || media.length === 0) return null;
  return (
    <div className={`px-4 pb-3 ${media.length > 1 ? 'grid grid-cols-2 gap-2' : ''}`}>
      {media.map((m, i) => (
        m.media_type === 'video' ? (
          <video key={i} src={mediaUrl(m.url)} controls className="w-full rounded-lg max-h-96 object-contain bg-black" data-testid={`post-video-${i}`} />
        ) : (
          <img key={i} src={mediaUrl(m.url)} alt="" className="w-full rounded-lg max-h-96 object-cover" loading="lazy" data-testid={`post-image-${i}`} />
        )
      ))}
    </div>
  );
};

const Community = () => {
  const { user } = useAuth();
  const { markSeen: markCommunitySeen } = useCommunityUnread();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [commentMentions, setCommentMentions] = useState({});
  const [postMentions, setPostMentions] = useState([]);
  const [editMentions, setEditMentions] = useState([]);
  const [highlightPostId, setHighlightPostId] = useState(null);
  const location = useLocation();
  const postRefs = useRef({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editMedia, setEditMedia] = useState([]);
  const [editUploading, setEditUploading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const fetchPosts = useCallback(async (p = 1) => {
    try {
      const res = await api.get(`/community/posts?page=${p}&limit=15`);
      setPosts(res.data.posts || []);
      setTotalPages(res.data.pages || 1);
      setPage(p);
    } catch {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); markCommunitySeen(); }, [fetchPosts, markCommunitySeen]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (mediaFiles.length + files.length > 4) {
      toast.error('Maximum 4 files per post');
      return;
    }

    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 50MB)`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/community/upload-media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push({
          url: res.data.url,
          media_type: res.data.media_type,
          filename: res.data.filename,
          preview: URL.createObjectURL(file),
        });
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setMediaFiles(prev => [...prev, ...uploaded]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeMedia = (idx) => {
    setMediaFiles(prev => {
      const copy = [...prev];
      if (copy[idx].preview) URL.revokeObjectURL(copy[idx].preview);
      copy.splice(idx, 1);
      return copy;
    });
  };

  const handleCreatePost = async () => {
    if (!newPost.trim() && mediaFiles.length === 0) return;
    setPosting(true);
    try {
      const media = mediaFiles.map(m => ({ url: m.url, media_type: m.media_type }));
      await api.post('/community/posts', { content: newPost, media, mentions: postMentions });
      setNewPost('');
      setPostMentions([]);
      mediaFiles.forEach(m => { if (m.preview) URL.revokeObjectURL(m.preview); });
      setMediaFiles([]);
      fetchPosts();
      toast.success('Posted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure? This will permanently remove the post.')) return;
    try {
      await api.delete(`/community/posts/${postId}`);
      fetchPosts(page);
      toast.success('Post deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const startEdit = (post) => {
    setEditingPostId(post.id);
    setEditContent(post.content || '');
    setEditMedia((post.media || []).map(m => ({ url: m.url, media_type: m.media_type, existing: true })));
    setEditMentions(post.mentions || []);
  };

  const cancelEdit = () => {
    editMedia.forEach(m => { if (m.preview) URL.revokeObjectURL(m.preview); });
    setEditingPostId(null);
    setEditContent('');
    setEditMedia([]);
    setEditMentions([]);
  };

  const handleEditFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (editMedia.length + files.length > 4) {
      toast.error('Maximum 4 files per post');
      return;
    }
    setEditUploading(true);
    const uploaded = [];
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 50MB)`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/community/upload-media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push({
          url: res.data.url,
          media_type: res.data.media_type,
          preview: URL.createObjectURL(file),
        });
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setEditMedia(prev => [...prev, ...uploaded]);
    setEditUploading(false);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const removeEditMedia = (idx) => {
    setEditMedia(prev => {
      const copy = [...prev];
      if (copy[idx].preview) URL.revokeObjectURL(copy[idx].preview);
      copy.splice(idx, 1);
      return copy;
    });
  };

  const handleSaveEdit = async () => {
    const trimmed = editContent.trim();
    if (!trimmed && editMedia.length === 0) {
      toast.error('Post must have text or media');
      return;
    }
    setSavingEdit(true);
    try {
      const media = editMedia.map(m => ({ url: m.url, media_type: m.media_type }));
      await api.put(`/community/posts/${editingPostId}`, { content: trimmed, media, mentions: editMentions });
      toast.success('Post updated');
      cancelEdit();
      fetchPosts(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save edit');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleLike = async (postId) => {
    try { await api.post(`/community/posts/${postId}/like`); fetchPosts(page); }
    catch { toast.error('Failed to like'); }
  };

  const handleDislike = async (postId) => {
    try { await api.post(`/community/posts/${postId}/dislike`); fetchPosts(page); }
    catch { toast.error('Failed to dislike'); }
  };

  const handleComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) return;
    try {
      await api.post(`/community/posts/${postId}/comment`, { text, mentions: commentMentions[postId] || [] });
      setCommentText({ ...commentText, [postId]: '' });
      setCommentMentions({ ...commentMentions, [postId]: [] });
      fetchPosts(page);
    } catch { toast.error('Failed to comment'); }
  };

  const handleReplyToComment = (postId, comment) => {
    const token = `@[${comment.author_name}](${comment.author_id}) `;
    setCommentText({ ...commentText, [postId]: token });
    setCommentMentions({
      ...commentMentions,
      [postId]: [{ user_id: comment.author_id, name: comment.author_name }],
    });
    setExpandedComments({ ...expandedComments, [postId]: true });
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="comment-input-${postId}"] textarea`);
      if (el) { el.focus(); el.setSelectionRange(token.length, token.length); }
    }, 50);
  };

  const handleCopyLink = (postId) => {
    const url = `${window.location.origin}/community?post=${postId}`;
    navigator.clipboard?.writeText(url)
      .then(() => toast.success('Link copied'))
      .catch(() => toast.error('Could not copy'));
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  // Scroll to ?post= on load
  useEffect(() => {
    if (loading || posts.length === 0) return;
    const params = new URLSearchParams(location.search);
    const target = params.get('post');
    if (!target) return;
    const el = postRefs.current[target];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightPostId(target);
      setTimeout(() => setHighlightPostId(null), 2500);
    }
  }, [loading, posts, location.search]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto px-4 py-8 w-full">
        {(user?.role === 'learner' || user?.role === 'service_provider') && (
          <Link
            to="/profile"
            className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-sky-50 hover:border-sky-300 hover:text-sky-700 transition-colors shadow-sm"
            data-testid="community-back-to-home-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        )}
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="community-title">Community Feed</h1>
          <p className="text-slate-600 text-sm mt-1">Connect with fellow carriers, drivers, and dispatchers</p>
        </div>

        {/* Create Post */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm" data-testid="create-post-section">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {user?.profile_picture ? (
                <img src={mediaUrl(user.profile_picture)} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="h-5 w-5 text-slate-500" />
              )}
            </div>
            <div className="flex-1">
              <MentionInput
                value={newPost}
                mentions={postMentions}
                onChange={(val, men) => { setNewPost(val); setPostMentions(men); }}
                placeholder="Share an update, ask a question, mention someone with @, paste a link…"
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                testId="post-content-input"
              />

              {/* Media Preview */}
              {mediaFiles.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap" data-testid="media-preview-area">
                  {mediaFiles.map((m, i) => (
                    <div key={i} className="relative group">
                      {m.media_type === 'video' ? (
                        <div className="w-20 h-20 rounded-lg bg-slate-900 flex items-center justify-center">
                          <Film className="h-6 w-6 text-white" />
                        </div>
                      ) : (
                        <img src={m.preview} alt="" className="w-20 h-20 rounded-lg object-cover" />
                      )}
                      <button onClick={() => removeMedia(i)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-media-${i}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between items-center mt-2">
                <div className="flex items-center gap-2">
                  <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple onChange={handleFileSelect} className="hidden" data-testid="media-file-input" />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading || mediaFiles.length >= 4}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="attach-media-btn">
                    {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                    Photo/Video
                  </button>
                  <span className="text-xs text-slate-400">{mediaFiles.length}/4</span>
                </div>
                <button
                  onClick={handleCreatePost}
                  disabled={posting || (!newPost.trim() && mediaFiles.length === 0)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="submit-post-btn"
                >
                  {posting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Post
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Posts */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No posts yet</p>
            <p className="text-sm text-slate-400">Be the first to share something!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post, idx) => {
              const roleConfig = ROLE_CONFIG[post.author_role] || ROLE_CONFIG.driver;
              const RoleIcon = roleConfig.icon;
              return (
                <motion.div
                  key={post.id}
                  ref={(el) => { if (el) postRefs.current[post.id] = el; }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className={`bg-white border rounded-xl shadow-sm overflow-hidden transition-all ${highlightPostId === post.id ? 'border-sky-400 ring-2 ring-sky-200' : 'border-slate-200'}`}
                  data-testid={`post-${post.id}`}
                >
                  {/* Post Header */}
                  <div className="px-4 pt-4 pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                          {post.author_picture ? (
                            <img src={mediaUrl(post.author_picture)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-4 w-4 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900 text-sm">{post.author_name}</span>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${roleConfig.bg} ${roleConfig.color}`}>
                              <RoleIcon className="h-3 w-3" />{roleConfig.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-slate-400">
                            <Clock className="h-3 w-3" />{timeAgo(post.created_at)}
                          </div>
                        </div>
                      </div>
                      {(post.author_id === user?.id || user?.is_admin) && editingPostId !== post.id && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEdit(post)} className="p-1.5 text-slate-300 hover:text-blue-500 rounded transition-colors" title="Edit post" data-testid={`edit-post-${post.id}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDelete(post.id)} className="p-1.5 text-slate-300 hover:text-red-500 rounded transition-colors" title="Delete post" data-testid={`delete-post-${post.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Edit Form */}
                  {editingPostId === post.id ? (
                    <div className="px-4 pb-4" data-testid={`edit-form-${post.id}`}>
                      <MentionInput
                        value={editContent}
                        mentions={editMentions}
                        onChange={(val, men) => { setEditContent(val); setEditMentions(men); }}
                        rows={3}
                        className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                        placeholder="Update your post..."
                        testId={`edit-content-${post.id}`}
                      />
                      {editMedia.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {editMedia.map((m, i) => (
                            <div key={i} className="relative group">
                              {m.media_type === 'video' ? (
                                <div className="w-20 h-20 rounded-lg bg-slate-900 flex items-center justify-center">
                                  <Film className="h-6 w-6 text-white" />
                                </div>
                              ) : (
                                <img src={m.preview || mediaUrl(m.url)} alt="" className="w-20 h-20 rounded-lg object-cover" />
                              )}
                              <button onClick={() => removeEditMedia(i)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`remove-edit-media-${i}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <input ref={editFileInputRef} type="file" accept="image/*,video/*" multiple onChange={handleEditFileSelect} className="hidden" data-testid={`edit-media-input-${post.id}`} />
                          <button onClick={() => editFileInputRef.current?.click()} disabled={editUploading || editMedia.length >= 4}
                            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors disabled:opacity-40"
                            data-testid={`edit-attach-media-${post.id}`}>
                            {editUploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                            Add Photo/Video
                          </button>
                          <span className="text-xs text-slate-400">{editMedia.length}/4</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={cancelEdit} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors" data-testid={`edit-cancel-${post.id}`}>
                            Cancel
                          </button>
                          <button onClick={handleSaveEdit} disabled={savingEdit || (!editContent.trim() && editMedia.length === 0)}
                            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            data-testid={`edit-save-${post.id}`}>
                            {savingEdit ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {post.content && (
                        <div className="px-4 pb-3">
                          <PostContent text={post.content} />
                          {post.edited && (
                            <span className="inline-block mt-1 text-[10px] text-slate-400 italic">edited</span>
                          )}
                        </div>
                      )}
                      <MediaDisplay media={post.media} />
                    </>
                  )}

                  {/* Actions Bar */}
                  <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-4">
                    <button onClick={() => handleLike(post.id)} className={`flex items-center gap-1.5 text-sm transition-colors ${post.liked_by_user ? 'text-blue-600 font-medium' : 'text-slate-500 hover:text-blue-600'}`} data-testid={`like-btn-${post.id}`}>
                      <ThumbsUp className="h-4 w-4" />{post.like_count || 0}
                    </button>
                    <button onClick={() => handleDislike(post.id)} className={`flex items-center gap-1.5 text-sm transition-colors ${post.disliked_by_user ? 'text-red-500 font-medium' : 'text-slate-500 hover:text-red-500'}`} data-testid={`dislike-btn-${post.id}`}>
                      <ThumbsDown className="h-4 w-4" />{post.dislike_count || 0}
                    </button>
                    <button onClick={() => toggleComments(post.id)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors" data-testid={`toggle-comments-${post.id}`}>
                      <MessageSquare className="h-4 w-4" />{post.comment_count || 0}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expandedComments[post.id] ? 'rotate-180' : ''}`} />
                    </button>
                    <button onClick={() => handleCopyLink(post.id)} className="ml-auto flex items-center gap-1.5 text-sm text-slate-500 hover:text-sky-600 transition-colors" title="Copy link to post" data-testid={`copy-link-${post.id}`}>
                      <Link2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Copy link</span>
                    </button>
                  </div>

                  {/* Comments */}
                  <AnimatePresence>
                    {expandedComments[post.id] && (
                      <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 space-y-3">
                          {post.comments?.map((c) => (
                            <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                              <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
                                {c.author_picture ? (
                                  <img src={mediaUrl(c.author_picture)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <User className="h-3.5 w-3.5 text-slate-500" />
                                )}
                              </div>
                              <div className={`flex-1 rounded-lg px-3 py-2 border ${c.is_admin_reply ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-100'}`}>
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className={`text-xs font-semibold ${c.is_admin_reply ? 'text-blue-700' : 'text-slate-800'}`}>
                                    {c.author_name}{c.is_admin_reply && ' (Admin)'}
                                  </span>
                                  <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
                                  {c.author_id !== user?.id && (
                                    <button onClick={() => handleReplyToComment(post.id, c)} className="ml-auto inline-flex items-center gap-0.5 text-[11px] text-slate-400 hover:text-sky-600 font-medium" data-testid={`reply-${c.id}`}>
                                      <Reply className="h-3 w-3" /> Reply
                                    </button>
                                  )}
                                </div>
                                <PostContent text={c.text} className="text-slate-700" />
                              </div>
                            </div>
                          ))}
                          {/* Add Comment */}
                          <div className="flex gap-2 pt-1 items-start" data-testid={`comment-input-${post.id}`}>
                            <div className="flex-1">
                              <MentionInput
                                value={commentText[post.id] || ''}
                                mentions={commentMentions[post.id] || []}
                                onChange={(val, men) => {
                                  setCommentText({ ...commentText, [post.id]: val });
                                  setCommentMentions({ ...commentMentions, [post.id]: men });
                                }}
                                placeholder="Write a comment, mention with @, paste a link…"
                                rows={2}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                                testId={`comment-textarea-${post.id}`}
                              />
                            </div>
                            <button onClick={() => handleComment(post.id)} className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors" data-testid={`submit-comment-${post.id}`}>
                              <Send className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => fetchPosts(p)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${p === page ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`} data-testid={`page-${p}`}>
                {p}
              </button>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Community;
